'use client';

import { useEffect, useState } from 'react';
import { api } from '@/services/api';

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'wallet' | 'withdrawal' | 'referral' | 'system';
  title: string;
  message: string;
  data: Record<string, unknown>;
  read: boolean;
  readAt: string | null;
  createdAt: string;
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/v1/notifications?page=1&limit=20');
      if (response) {
        const data = response as any;
        if (data.data) {
          setNotifications(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="animate-pulse h-8 w-48 bg-slate-800 rounded" />
          <div className="animate-pulse h-10 w-32 bg-slate-800 rounded" />
        </div>
        <div className="glass">
          <div className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 w-full bg-slate-800/50 rounded animate-pulse" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Notifications</h1>
          <p className="text-slate-400 mt-1">Stay updated with your account activity</p>
        </div>
      </div>

      <div className="glass">
        <div className="p-4">
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className="p-4 rounded-xl border transition-all border-slate-700/50 hover:border-cyan-500/30"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-slate-700/50">
                      <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 01-3.46 0" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-white">{notification.title}</h4>
                          <span className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded">{notification.type}</span>
                          {!notification.read && (
                            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                          )}
                        </div>
                        <p className="text-xs text-slate-500 whitespace-nowrap">
                          {notification.createdAt}
                        </p>
                      </div>
                      <p className="text-slate-300 mt-1">{notification.message}</p>
                      {Object.keys(notification.data).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {Object.entries(notification.data).map(([key, value]) => (
                            <span key={key} className="px-2 py-0.5 text-xs bg-slate-800 border border-slate-700 rounded text-slate-400">
                              {key}: {String(value)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button className="text-slate-400 hover:text-cyan-400 p-1">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <svg className="h-12 w-12 mx-auto mb-4 text-slate-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                <p className="text-slate-400">No notifications found</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
            <p className="text-sm text-slate-400">
              Showing {notifications.length} notifications
            </p>
            <div className="flex items-center gap-2">
              <button disabled className="px-3 py-1 text-sm bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <span className="text-sm text-slate-300">Page 1</span>
              <button className="px-3 py-1 text-sm bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationsPage;
